import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  AlertCircle,
  Ban,
  Eye,
  EyeOff,
  Info,
  Loader2,
  ShieldAlert,
  ShieldCheck,
  UserSearch,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuthStore } from "@/store/useAuthStore";
import { useDrawerA11y } from "@/hooks/useDrawerA11y";
import {
  fetchViewAsPreview,
  fetchViewAsTargets,
  startViewAs,
  type ViewAsTarget,
} from "@/api/adminCenter";
import { buildViewAsTabUrl } from "@/lib/viewAs";

/**
 * D-044 S5 — #5 Перегляд доступу / view-as (VISUAL v1.1, SECURITY-critical).
 * (A) Preview: resolver-COMPUTE both-ways == live ACL (AC-080), exact counts +
 * computedAt (MP6), export-рядок завжди, MP1 None-selected стан.
 * (B) View-as: start=CONFIRM (MP2) → server-side session (AC-081; фронт
 * отримує bearer-пару, raw targetUserId далі НЕ їде) → повне перезавантаження
 * → увесь app як target + global banner. Guardrails (MP4) server-enforced —
 * тут лише дзеркало (disabled з причиною). MP9 start-error стани.
 */

export function AdminViewAsPage() {
  const perms = useAuthStore((s) => s.permissions);
  const canView = !!perms?.canViewAdminCenter;
  const [params] = useSearchParams();
  const ended = params.get("ended");

  const [sel, setSel] = useState<string>("");
  const [confirm, setConfirm] = useState<ViewAsTarget | null>(null);
  const [startErr, setStartErr] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);

  const targetsQ = useQuery({
    queryKey: ["adminCenter", "viewAsTargets"],
    queryFn: ({ signal }) => fetchViewAsTargets(signal),
    staleTime: 30_000,
    enabled: canView,
  });
  const previewQ = useQuery({
    queryKey: ["adminCenter", "viewAsPreview", sel],
    queryFn: ({ signal }) => fetchViewAsPreview(sel, signal),
    enabled: canView && sel !== "",
    staleTime: 0, // MP6: перерахунок на кожен вибір, computedAt чесний
  });

  if (!canView) {
    return (
      <div className="p-4 md:p-6">
        <div className="mx-auto w-full max-w-[640px] rounded-lg border bg-muted/30 p-8 text-center">
          <ShieldAlert className="mx-auto mb-2 h-5 w-5 text-muted-foreground" />
          <p className="text-sm font-medium">Немає доступу до Адмін-центру</p>
          <p className="mt-1 text-xs text-muted-foreground">Розділ доступний лише адміністраторам.</p>
        </div>
      </div>
    );
  }

  const targets = targetsQ.data?.targets ?? [];
  const target = targets.find((t) => t.id === sel);
  const preview = previewQ.data;

  // New-tab модель: view-as відкривається в ОКРЕМІЙ вкладці; адмінська вкладка
  // (ця) НЕ підмінюється. window.open синхронно (user-gesture, не блокується
  // popup-блокером), потім async start, потім ведемо вкладку на bearer-URL.
  const doStart = async (t: ViewAsTarget) => {
    setStarting(true);
    setStartErr(null);
    const tab = window.open("about:blank", "_blank"); // синхронно — зберігає gesture
    try {
      const res = await startViewAs(t.id);
      const url = buildViewAsTabUrl({
        sessionId: res.sessionId,
        secret: res.secret,
        targetName: res.target.name,
        expiresAt: res.expiresAt,
      });
      if (tab && !tab.closed) {
        tab.location.replace(url); // вкладка стартує як target (swap per-tab)
        setStarting(false);
        setConfirm(null);
      } else {
        // popup заблоковано — НЕ свопимо адмін-вкладку; просимо дозволити
        setStartErr(
          "Вкладку перегляду заблоковано браузером. Дозвольте спливаючі вікна для цього сайту і повторіть — режим перегляду відкривається в новій вкладці, щоб не змінювати вашу адмін-сесію.",
        );
        setStarting(false);
        setConfirm(null);
      }
    } catch (e) {
      if (tab && !tab.closed) tab.close();
      // MP9: target-ineligible / create-failed — server message
      setStartErr(e instanceof Error ? e.message : "Не вдалося створити сесію перегляду.");
      setStarting(false);
      setConfirm(null);
    }
  };

  return (
    <div className="p-4 md:p-6">
      <div className="mx-auto w-full max-w-[1000px] space-y-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
            <Eye className="h-5 w-5" /> Перегляд доступу / View-as
          </h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Превʼю = той самий AccessResolver, що live ACL · view-as = server-side сесія, READ-ONLY,
            15 хв, аудит start/end.
          </p>
        </div>

        {ended && (
          <div className="flex items-center gap-2 rounded-lg bg-warning px-3 py-2.5 text-sm text-warning-foreground" role="status">
            <Info className="h-4 w-4 shrink-0" />
            {ended === "manual"
              ? "Сесію перегляду завершено. Ви знову працюєте як адміністратор."
              : "Сесію перегляду завершено (час вийшов або закрита). Ви знову працюєте як адміністратор."}
          </div>
        )}

        {startErr && (
          <div className="flex items-start gap-2 rounded-lg bg-destructive-soft px-3 py-2.5 text-sm text-destructive" role="alert">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              <b>Не вдалося розпочати перегляд.</b> {startErr}{" "}
              {target?.eligible && (
                <button type="button" className="underline" onClick={() => setConfirm(target)}>
                  Повторити
                </button>
              )}
            </span>
          </div>
        )}

        <div className="rounded-card border bg-card p-4">
          <label className="block max-w-sm">
            <span className="mb-1 block text-xs text-muted-foreground">Користувач</span>
            {targetsQ.isLoading ? (
              <Skeleton className="h-9 w-full" />
            ) : (
              <select
                value={sel}
                onChange={(e) => {
                  setSel(e.target.value);
                  setStartErr(null);
                }}
                className="w-full rounded-md border border-input bg-background px-2.5 py-2 text-sm"
              >
                <option value="">— оберіть користувача —</option>
                {targets.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                    {t.tierLabel ? ` · ${t.tierLabel}` : ""}
                    {!t.eligible ? " · (вхід заблоковано)" : ""}
                  </option>
                ))}
              </select>
            )}
          </label>

          {/* MP1: None-selected */}
          {sel === "" && (
            <div className="mt-4 rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
              <UserSearch className="mx-auto mb-2 h-6 w-6" />
              Оберіть користувача, щоб побачити його доступ
            </div>
          )}

          {sel !== "" && previewQ.isLoading && (
            <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
              <Skeleton className="h-40 w-full" />
              <Skeleton className="h-40 w-full" />
            </div>
          )}
          {sel !== "" && previewQ.isError && (
            <div className="mt-4 text-sm text-destructive">
              Не вдалося обчислити превʼю.{" "}
              <button type="button" className="underline" onClick={() => previewQ.refetch()}>
                Повторити
              </button>
            </div>
          )}

          {preview && (
            <>
              {/* AC-080: both-ways resolver-compute */}
              <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                <div className="rounded-lg bg-success p-3">
                  <p className="mb-2 flex items-center gap-1.5 text-sm font-medium text-success-foreground">
                    <Eye className="h-4 w-4" /> Побачить
                  </p>
                  <ul className="space-y-1.5 text-xs text-success-foreground">
                    {preview.see.map((r) => (
                      <li key={r.key} className="flex items-start gap-1.5">
                        <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-success-foreground" />
                        {r.label}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="rounded-lg bg-muted/50 p-3">
                  <p className="mb-2 flex items-center gap-1.5 text-sm font-medium text-muted-foreground">
                    <EyeOff className="h-4 w-4" /> НЕ побачить
                  </p>
                  <ul className="space-y-1.5 text-xs text-muted-foreground">
                    {preview.notSee.map((r) => (
                      <li key={r.key} className="flex items-start gap-1.5">
                        <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-muted-foreground" />
                        {r.label}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* MP6: computedAt — явний resolver-compute */}
              <p className="mt-2 flex items-center gap-1.5 text-[11px] text-muted-foreground">
                <ShieldCheck className="h-3.5 w-3.5" />
                Обчислено {preview.computedAt} · AccessResolver (== live ACL) · counts точні
              </p>

              <div className="mt-3 flex flex-wrap items-center gap-3 border-t pt-3">
                {target?.eligible ? (
                  <>
                    <Button size="sm" disabled={starting} onClick={() => setConfirm(target)}>
                      {starting ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Eye className="mr-1 h-4 w-4" />}
                      Увійти як (нова вкладка, read-only)
                    </Button>
                    <span className="text-[11px] text-muted-foreground">
                      відкриється в новій вкладці · ваша адмін-сесія тут не зміниться · 15 хв · усі
                      write-дії → 403 · audit start/end
                    </span>
                  </>
                ) : (
                  <>
                    <Button size="sm" disabled>
                      <EyeOff className="mr-1 h-4 w-4" /> Увійти як
                    </Button>
                    {/* §0: disabled з причиною (server-enforced) */}
                    <span className="flex items-center gap-1 text-[11px] text-destructive">
                      <Ban className="h-3.5 w-3.5" />
                      {target?.reason ?? preview.ineligibleReason ?? "Вхід заблоковано."} (server-enforced)
                    </span>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* MP2: start = CONFIRM */}
      {confirm && (
        <ConfirmStartDialog
          target={confirm}
          busy={starting}
          onCancel={() => setConfirm(null)}
          onYes={() => doStart(confirm)}
        />
      )}
    </div>
  );
}

function ConfirmStartDialog({
  target,
  busy,
  onCancel,
  onYes,
}: {
  target: ViewAsTarget;
  busy: boolean;
  onCancel: () => void;
  onYes: () => void;
}) {
  const panelRef = useDrawerA11y(true, onCancel);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-5">
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Підтвердження режиму перегляду"
        className="w-full max-w-sm rounded-card border bg-card p-4 shadow-xl outline-none"
      >
        <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold">
          <Eye className="h-4 w-4 text-warning-foreground" /> Увійти в режим перегляду?
        </h3>
        <p className="mb-4 text-sm text-muted-foreground">
          Відкрити режим перегляду як <b>{target.name}</b> у <b>новій вкладці</b>? Режим
          <b> READ-ONLY</b> (15 хв), усі дії записуються в журнал аудиту. Ця адмін-вкладка лишається
          незмінною.
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onCancel}>
            Скасувати
          </Button>
          <Button size="sm" disabled={busy} onClick={onYes}>
            {busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            Підтвердити
          </Button>
        </div>
      </div>
    </div>
  );
}
